import { ContactMoldKind, CountryEnum, MoldConcentration } from '../../enums';
import { AsbestosFinding } from '../../sampleResults/asbestos';
import { DEFAULT_GERMAN_DATE_FORMAT } from '../../utils/dayjs';

export default {
  language: {
    'de': 'Deutsch',
    'en-US': 'English',
    'nl': 'Nederlands',
  },
  date: {
    format: DEFAULT_GERMAN_DATE_FORMAT,
  },
  common: {
    back: 'Zurück',
    logout: 'Abmelden',
    kcProfile: 'Profil bearbeiten',
    downloadPdf: 'Herunterladen',
    pageTitles: {
      callback: 'Anmeldung...',
      refresh: 'Aktualisierung...',
      activateSampleCode: 'Probencode aktivieren',
      activateSample: 'Probe aktivieren',
      editSenderAddress: 'Absendeadresse bearbeiten',
      addSampleComment: 'Kommentare zur Probe hinzufügen',
      generateSampleCodes: 'Probengenerierung',
      accountOverview: 'Accountübersicht',
      resultDetails: 'Ergebnisdetails',
    },
    nav: {
      overview: 'Übersicht',
      onlineshop: 'Onlineshop',
      onlineshopUrl: 'https://ivario.com/de-de',
      faq: 'Fragen & Beratung',
      faqUrl: 'https://ivario.com/de-de/pages/hilfe',
      support: 'Support',
      supportEmail: 'support@ivario.com',
      supportPhone: '+49 (0)40-180 24 111',
      supportPhoneInfo: '(Mo–Fr 09–17 Uhr)',
      showPhone: 'true',
      myAccount: 'Mein Konto',
      myProfile: 'Mein Profil',
      myOrders: 'Meine Bestellungen',
      myOrdersUrl: 'https://account.ivario.com',
      logOut: 'Abmelden',
    },
  },
  errors: {
    emptyValue: 'Dieses Feld darf nicht leer sein.',
    saveAddress:
      'Fehler beim Speichern der Absenderadresse oder beim Erstellen der Versandetikette. Bitte prüfen Sie Ihre Eingaben und versuchen Sie es erneut.',
  },
  component: {
    parameterDetail: {
      oneTab: 'Informationen',
      twoTab: 'Gesundheit',
      threeTab: 'Ernährung',
      fourTab: 'Familie',
      noOneInfoAvailable:
        'Für diesen Parameter sind keine Informationen verfügbar.',
      noTwoInfoAvailable:
        'Für diesen Parameter sind keine Informationen zum Thema Gesundheit verfügbar.',
      noThreeInfoAvailable:
        'Für diesen Parameter sind keine Informationen zum Thema Ernährung verfügbar.',
      noFourInfoAvailable:
        'Für diesen Parameter sind keine Informationen zum Thema Familie verfügbar.',
    },
    sampleTeaser: {
      activationDate: 'Aktivierungsdatum',
      ACTIVATED: 'Aktiviert',
      ARRIVED_IN_LAB: 'Im Labor angekommen',
      SHIPPED: 'Versand durch Kunde',
      DONE: 'Ergebnisse verfügbar',
    },
    shipmentInstructions: {
      shipmentLabelTitle: 'Versandlabel für die Laboreinsendung',
      downloadLabel: 'Als PDF herunterladen',
      saveQrCode: 'Als QR-Code (Handy)',
      shipmentLabelHint:
        '→ Beide Buttons laden dasselbe Versandlabel – einmal als PDF zum Drucken, einmal als QR-Code fürs Handy (z.B. Packstation)',
      addAddress: 'Adresse hinzufügen',
      foreignShippingInstructions:
        'Informationen darüber, wie Ihre Probe an unser Labor geschickt werden sollte, finden Sie in diesem',
      foreignShippingInstructionsLink: 'FAQ-Artikel.',
    },
    sampleDetails: {
      sampleId: 'Probennummer',
      instructions: {
        image: 'Probenentnahmeanleitung',
        nextSteps: 'Nächste Schritte',
        trackingLink: 'Sendungsverfolgung',
        step1: {
          title: 'Probenentnahme',
          point1: 'Öffnen Sie das Probenentnahme-Kit',
          point2: 'Folgen Sie der beigelegten Anleitung',
          point3: 'Entnehmen Sie die Probe gemäß den Anweisungen',
        },
        step2: {
          title: 'Versand',
          point1: 'Verpacken Sie die Probe sicher',
          point2: 'Kleben Sie das Versandetikett auf',
          point3: 'Bringen Sie das Paket zur Post',
          point4: 'Senden Sie es an unser Labor',
        },
      },
      buttons: {
        downloadLabel: 'Versandetikett herunterladen',
        downloadCode: 'Probencode herunterladen',
      },
      status: {
        title: 'Status',
        ACTIVATED: 'Aktiviert',
        ARRIVED_IN_LAB: 'Im Labor angekommen',
        SHIPPED: 'Versendet',
        DONE: 'Ergebnisse verfügbar',
        undefined: 'kein Status',
      },
      unknownField: '-',
      fields: {
        street: 'Straße',
        postalCode: 'PLZ',
        region: 'Region',
        houseNumber: 'Hausnummer',
        createdAt: 'Erstellt am',
        samplingPerson: 'Probenehmer',
        address: 'Adresse',
        samplingLocation: 'Probenentnahmeort',
        samplingDate: 'Entnahmedatum',
        samplingTime: 'Entnahmeuhrzeit',
        companyName: 'Firmenname',
        city: 'Stadt',
        notes: 'Notizen',
      },
      sampler: 'Probenehmer',
      edit: 'Bearbeiten',
    },
    parameterList: {
      remImageCaption:
        'Das Bild des Rasterelektronenmikroskops zeigt einen vom Labormitarbeiter ausgewählten Ausschnitt der Objektoberfläche auf der die Asbestfasern oder künstlichen Mineralfasern, falls diese in Ihrer Probe gefunden wurden, dargestellt werden. Asbestfasern sind in der Regel zerzaust und ähneln vom Bild, umgangssprachlich ausgedrückt, Wollfasern oder Zuckerwatte. Künstliche Mineralfasern erinnern vom Bild her eher an ungekochte Spaghetti, da sie in der Regel stäbchenförmig und gerade sind. Im unteren Rand des Bildes sehen Sie den Maßstab angegeben, um einen Vergleich zur Fasergröße zu haben.',
      edxImageCaption:
        'Es handelt sich hierbei um ein Diagramm der energiedispersiven Röntgenspektroskopie, kurz EDX. Das von Ihnen eingesendete Material wird hierbei auf die vorhandenen Elemente untersucht. Das Spektrum zeigt das Niveau der Energie, welche bei Elektronenübergängen innerhalb der Atome freigesetzt wird. Das Ergebnis wird im Labor mit dem REM Bild abgeglichen, um sicher zu stellen, ob es sich um Asbestfasern, künstliche Mineralfasern oder unschädliches Material handelt. Die einzelnen Felder im Diagramm entsprechen den Abkürzungen der Elemente im Periodensystem. Das EDX-Spektrum gibt allerdings keine Auskunft über die Faseranzahl.',
      moreInfoOnParam: 'Mehr Informationen zum Parameter',
      errors: {
        noMoldDataAvailable: 'Keine Schimmeldaten verfügbar',
      },
      asbestosAnalysisError: {
        title: 'Fehler bei der Asbestanalyse',
        description:
          'Bitte kontaktieren Sie unseren Kundenservice für weitere Informationen.',
        backToProfile: 'Zurück zum Profil',
      },
      moldAnalysisError: {
        title: 'Fehler bei der Schimmelanalyse',
        description:
          'Bitte kontaktieren Sie unseren Kundenservice für weitere Informationen.',
        backToProfile: 'Zurück zum Profil',
      },
      asbestosDetection: {
        asbestosTitle: 'Asbest',
        kmfTitle: 'KMF',
        asbestosDetection: 'Asbestnachweis',
        kmfDetection: 'KMF-Nachweis',
        samplePreparation: 'Probenvorbereitung',
        procedure: 'Verfahren',
        proofThreshold: 'Nachweisgrenze',
        finding: 'Befund',
      },
      moldValues: {
        [MoldConcentration.NO_MOLD]: 'Kein Schimmel nachgewiesen',
        [MoldConcentration.LOW_MOLD]:
          'Niedrige Schimmelkonzentration nachgewiesen',
        [MoldConcentration.MEDIUM_MOLD]:
          'Mäßige Schimmelkonzentration nachgewiesen',
        [MoldConcentration.HIGH_MOLD]:
          'Hohe Schimmelkonzentration nachgewiesen',
        NA: 'Probe nicht analysierbar oder ausgetrocknet',
      },
      moldConcentration: {
        [MoldConcentration.NO_MOLD]: 'Keine',
        [MoldConcentration.LOW_MOLD]: 'Niedrige',
        [MoldConcentration.MEDIUM_MOLD]: 'Mäßige',
        [MoldConcentration.HIGH_MOLD]: 'Hohe',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'niedriger Anflug',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'mäßiger Anflug',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.ANFLUG}`]:
          'hoher Anflug',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.BEFALL}`]:
          'niedriger Befall',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.BEFALL}`]:
          'mäßiger Befall',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.BEFALL}`]:
          'hoher Befall',
        [`${MoldConcentration.LOW_MOLD}_${ContactMoldKind.NONE}`]: 'niedrige',
        [`${MoldConcentration.MEDIUM_MOLD}_${ContactMoldKind.NONE}`]: 'mäßige',
        [`${MoldConcentration.HIGH_MOLD}_${ContactMoldKind.NONE}`]: 'hohe',
        name: 'Belastung',
      },
      moldExposure: {
        name: 'Belastung',
      },
      keinBefund: 'Kein Befund',
      CFU: 'Koloniebildende Einheiten (CFUs)',
      moldShortInfo: {
        [MoldConcentration.NO_MOLD]:
          'In ihrer Probe wurde kein Schimmel nachgewiesen.',
        [MoldConcentration.LOW_MOLD]:
          'In ihrer Probe wurde eine niedrige Konzentration Schimmel nachgewiesen.',
        [MoldConcentration.MEDIUM_MOLD]:
          'In ihrer Probe wurde eine mäßige Konzentration Schimmel nachgewiesen.',
        [MoldConcentration.HIGH_MOLD]:
          'In ihrer Probe wurde eine hohe Konzentration Schimmel nachgewiesen.',
      },
      asbestosValues: {
        [AsbestosFinding.CHRYSOTIL_DETECTED]: 'Chrysotilasbest nachgewiesen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Chrysotil- und Amphibolasbest (Anthophyllit) nachgewiesen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Chrysotil- und Amphibolasbest (Krokydolith) nachgewiesen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_AMOSITE_DETECTED]:
          'Chrysotil- und Amphibolasbest (Amosit) nachgewiesen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_TREMOLITE_DETECTED]:
          'Chrysotil- und Amphibolasbest (Tremolit) nachgewiesen',
        [AsbestosFinding.CHRYSOTIL_AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Chrysotil- und Amphibolasbest (Aktinolith) nachgewiesen',
        [AsbestosFinding.NO_ASBESTOS_DETECTED]: 'Asbest nicht nachgewiesen',
        [AsbestosFinding.NO_ASBESTOS_INORGANIC_FIBER]:
          'Asbest nicht nachgewiesen (sonstige anorganische Faser)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER]:
          'Asbest nicht nachgewiesen (organische Faser)',
        [AsbestosFinding.NO_ASBESTOS_ORGANIC_FIBER_ALT]:
          'Asbest nicht nachgewiesen (sonstige Fasern nachgewiesen)',
        [AsbestosFinding.NO_ASBESTOS_TALK_DETECTED]:
          'Asbest nicht nachgewiesen (Talk nachgewiesen)',
        [AsbestosFinding.AMPHIBOLE_TREMOLITE_DETECTED]:
          'Amphibolasbest (Tremolit) nachgewiesen',
        [AsbestosFinding.AMPHIBOLE_AMOSITE_DETECTED]:
          'Amphibolasbest (Amosit) nachgewiesen',
        [AsbestosFinding.AMPHIBOLE_ANTHOPHYLLITE_DETECTED]:
          'Amphibolasbest (Anthophyllit) nachgewiesen',
        [AsbestosFinding.AMPHIBOLE_CROCIDOLITE_DETECTED]:
          'Amphibolasbest (Krokydolith) nachgewiesen',
        [AsbestosFinding.AMPHIBOLE_ACTINOLITE_DETECTED]:
          'Amphibolasbest (Aktinolith) nachgewiesen',
        [AsbestosFinding.TRACE]: 'Spuren',
        [AsbestosFinding.PERCENT_1_TO_5]: '1-5%',
        [AsbestosFinding.PERCENT_5_TO_20]: '5-20%',
        [AsbestosFinding.PERCENT_20_TO_50]: '20-50%',
        [AsbestosFinding.PERCENT_OVER_50]: '> 50%',
        [AsbestosFinding.NOT_POSSIBLE]: '',
        [AsbestosFinding.MINUS]: '',
        [AsbestosFinding.AMF_NOT_DETECTED]: 'KMF nicht nachgewiesen',
        [AsbestosFinding.AMF_DETECTED_NON_WHO]:
          'KMF nachgewiesen (keine WHO-Fasern)',
        [AsbestosFinding.AMF_DETECTED_WHO]: 'KMF nachgewiesen (WHO-Fasern)',
      },
      notAnalyzable: 'n.a.',
      notAnalyzableLong: 'Nicht analysierbar',
      notDetectable: 'n.n.',
      notDetectableLong: 'Nicht nachweisbar',
      exceededRegulation: 'Grenzwert überschritten',
      closeToExceedingRegulation: 'Nahe am Grenzwert',
      parameter: 'Parameter',
      value: 'Wert',
      evaluation: 'Bewertung',
      status: 'Status',
      details: 'Details anzeigen',
      recommendations: 'Empfehlungen',
      statusTypes: {
        error: 'Problematisch',
        warning: 'Am Grenzwert',
        success: 'Normal',
        unset: 'Nicht gesetzt',
        notAnalyzable: 'Nicht analysierbar',
      },
      sort: {
        label: 'Sortieren nach',
        CATEGORY_ASC: 'Parametergruppe (A-Z)',
        CATEGORY_DESC: 'Parametergruppe (Z-A)',
        ALPHABET_ASC: 'Alphabetisch (A-Z)',
        ALPHABET_DESC: 'Alphabetisch (Z-A)',
        DEVIATION_ASC: 'Abweichung vom Grenzwert (aufsteigend)',
        DEVIATION_DESC: 'Abweichung vom Grenzwert (absteigend)',
      },
    },
    impersonationBanner: {
      message:
        'Sie befinden sich auf einer Kunden-Support-Ansicht. Einige Funktionen, wie das Bearbeiten von Proben-Daten, sind hier nicht verfügbar.',
    },
    announcementBar: {
      readMore: 'Mehr anzeigen',
      readLess: 'Weniger anzeigen',
    },
  },
  view: {
    commentsForm: {
      title: 'Fügen Sie Kommentare zu Ihrer Probe hinzu',
      description:
        'Diese Daten werden in Ihren Analyseergebnissen sichtbar sein.',
      samplingPoint: 'Entnahmestelle',
      companyName: 'Firmenname',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      postalCode: 'Postleitzahl',
      city: 'Ort',
      samplingDate: 'Entnahmedatum',
      samplingTime: 'Entnahmeuhrzeit',
      sampler: 'Probenehmende Person',
      notes: 'Notizen',
      saveButton: 'Kommentare abspeichern',
      back: '$t(common:back)',
    },
    error: {
      apology: 'Entschuldigung, es ist ein Fehler aufgetreten.',
      details: '{{stack}}',
      buttons: {
        backToProfile: 'Zurück zur Profilsübersicht',
        logout: '$t(common:logout)',
      },
      empty: 'Die Eingabe darf nicht leer sein.',
      connectionError: 'Fehler bei der Datenübertragung',
      503: 'Dienst nicht erreichbar',
    },
    profile: {
      welcome: 'Willkommen, {{name}}!',
      addSample: 'Probencode hinzufügen',
      title: 'Alle Proben',
      result: 'Ergebnis',
      details: 'Details',
      latestSample: {
        nextStep: 'Nächste Schritte für die Probe',
        title: 'Jetzt Probe absenden: {{productName}}',
        infoText:
          'Ihre Probe wurde erfolgreich aktiviert. Nehmen Sie als Nächstes eine Probe, wie in der Packungsbeilage beschrieben, und senden Sie diese an unser Labor.',
      },
    },
    registerSample: {
      title: 'Probencode aktivieren',
      subtitle:
        'Geben Sie Ihren Probencode ein, um Ihre Analyse zu aktivieren und mit der Einsendung fortzufahren.',
      hintTitle: 'Wo finde ich meinen Probencode?',
      hintDescription:
        'Er befindet sich auf der ersten Seite der mitgelieferten Anleitung.',
      code: 'Probencode',
      codePlaceholder: 'z. B. sabu3-xuwe2-vafu2',
      inputNote: 'Groß- und Kleinschreibung beachten',
      verify: 'Weiter',
      invalidCode: 'Ungültiger Probencode',
      cancelledCode:
        'Dieser Probencode kann nicht aktiviert werden. Bitte wenden Sie sich an den Support, wenn Sie Fragen haben.',
      empty: '$t(view:error.empty)',
    },
    landing: {
      tabTitle: 'IVARIO-Login',
      metaDescription:
        'Sehen Sie die Laborergebnisse Ihrer Wasseranalyse oder Ihres Asbest-Tests sicher und schnell online ein. Jetzt im IVARIO Kundenkonto anmelden!',
      pageTitle: 'Probe aktivieren',
      pageSub:
        'Registrieren Sie sich oder melden Sie sich an, um Ihre Probe einzusenden.',
      newUser: {
        title: 'Ich bin neu',
        description:
          'Noch kein Konto? Auch wenn Sie bereits bei uns bestellt haben – für das Analysecenter ist eine einmalige Registrierung nötig.',
        nextStepsLabel: "So geht's weiter:",
        steps: [
          'E-Mail bestätigen',
          'Probencode eingeben',
          'Versandetikett erstellen',
          'Ergebnisse erhalten',
        ],
        cta: 'Jetzt registrieren',
      },
      existingUser: {
        title: 'Ich habe bereits ein Konto',
        description: 'Ich habe bereits ein Konto im IVARIO Analysecenter.',
        info: [
          'Proben einsehen & verwalten',
          'Analysebericht herunterladen',
          'Versandetikett erstellen',
        ],
        cta: 'Login',
      },
      oldResults:
        'Suchen Sie nach alten Analyseergebnissen von Proben, die vor Oktober 2025 entnommen wurden?',
      helpPrefix: 'Fragen?',
      helpLink: 'Kundenservice kontaktieren',
      supportUrl: 'https://ivario.com/de-de/a/faq',
      headerLangLabel: 'Sprache:',
      overlay: {
        title: 'Sprache erkannt',
        sub: 'Wir haben <strong>Deutsch</strong> als Ihre Webbrowsersprache erkannt.<br>Möchten Sie in dieser Sprache fortfahren?',
        confirm: 'In Deutsch fortfahren',
        or: 'oder andere Sprache wählen',
        otherLabel: 'Andere Sprache:',
        selectBtn: 'Mit gewählter Sprache fortfahren',
      },
    },
    senderAddress: {
      title: 'Neue Probe',
      success: 'Erfolgreich registriert',
      failure: 'Registrierung fehlgeschlagen',
      save: 'Speichern',
    },
    result: {
      title: 'Analyseergebnis: {{id}}',
      loading: 'Lade Ergebnis...',
    },
    internal: {
      generateSampleCodes: {
        title: 'Probencodes generieren',
        selectProduct: 'Wähle ein Produkt',
        numberOfCodes: 'Wie viele Probencodes möchten Sie generieren?',
        generatedFor: 'Generiert für',
        generatedForPlaceholder: 'z.B. Obi, Charge 1002, Messe Hannover',
        generate: 'Probencodes generieren',
        close: 'Schließen',
        error: 'Fehler bei der Probencodegenerierung',
        minimumCodes: 'Die Anzahl der Probencodes muss mindestens eins sein.',
        generatedForRequired:
          'Bitte geben Sie mindestens fünf Zeichen ein, wofür die Probencodes generiert werden.',
      },
    },
    footer: {
      shipping: 'Versand und Zahlung',
      dataPrivacy: 'Datenschutz',
      blog: 'IVARIO-Blog',
      experience: 'Kundenbewertungen',
      testResult: 'Zum alten System vor Oktober 2025',
      agb: 'AGB und Widerruf',
      faq: 'FAQ und Kontakt',
      waterWiki: 'Trinkwasser-Blog',
      imprint: 'Impressum',
      testCompare: 'Wassertestvergleich',
      moldWiki: 'Schimmel-Blog',
      contact: 'Kontakt',
      accreditation: 'Akkreditierung',
      affiliate: 'Affiliate-Partner',
      asbestos: 'Asbestfachberater',
      agbUrl: 'https://ivario.com/de-de/policies/terms-of-service',
      dataPrivacyUrl: 'https://ivario.com/de-de/policies/privacy-policy',
    },
    senderAddressForm: {
      title: 'Absenderadresse hinzufügen',
      subtitle:
        'Um ein Versandetikett zu erstellen, muss zunächst eine Absendeadresse hinzugefügt werden. Kostenfreie Versandetiketten können nur für die Länder Deutschland, Österreich und die Niederlande erstellt werden.',
      lastName: 'Nachname',
      firstName: 'Vorname',
      street: 'Straße',
      houseNumber: 'Hausnummer',
      postalCode: 'Postleitzahl',
      city: 'Ort',
      country: 'Land',
      companyName: 'Firmenname (optional)',
      saveSenderAddress: 'Absendeadresse speichern',
    },
  },
  countries: {
    [CountryEnum.Germany]: 'Deutschland',
    [CountryEnum.Austria]: 'Österreich',
    [CountryEnum.Belgium]: 'Belgien',
    [CountryEnum.Bulgaria]: 'Bulgarien',
    [CountryEnum.Croatia]: 'Kroatien',
    [CountryEnum.Cyprus]: 'Zypern',
    [CountryEnum['Czech Republic']]: 'Tschechische Republik',
    [CountryEnum.Denmark]: 'Dänemark',
    [CountryEnum.Estonia]: 'Estland',
    [CountryEnum.Finland]: 'Finnland',
    [CountryEnum.France]: 'Frankreich',
    [CountryEnum.Greece]: 'Griechenland',
    [CountryEnum.Hungary]: 'Ungarn',
    [CountryEnum.Ireland]: 'Irland',
    [CountryEnum.Italy]: 'Italien',
    [CountryEnum.Latvia]: 'Lettland',
    [CountryEnum.Lithuania]: 'Litauen',
    [CountryEnum.Luxembourg]: 'Luxemburg',
    [CountryEnum.Malta]: 'Malta',
    [CountryEnum.Netherlands]: 'Niederlande',
    [CountryEnum.Poland]: 'Polen',
    [CountryEnum.Portugal]: 'Portugal',
    [CountryEnum.Romania]: 'Rumänien',
    [CountryEnum.Slovakia]: 'Slowakei',
    [CountryEnum.Slovenia]: 'Slowenien',
    [CountryEnum.Spain]: 'Spanien',
    [CountryEnum.Sweden]: 'Schweden',
    [CountryEnum.Switzerland]: 'Schweiz',
  },
  toasts: {
    sampleClaimed: {
      title: 'Erfolgreich aktiviert',
      subTitle: 'Ihre Probe wurde erfolgreich aktiviert',
    },
    sampleClaimedFailed: {
      title: 'Fehler',
      subTitle: 'Ihre Probe konnte nicht aktiviert werden',
    },
  },
  legacy: {
    register: 'Haben Sie Ihre Probe bereits im alten System aktiviert? ',
    registerLink: 'Hier einloggen\u00A0→',
    sampleList:
      'Wenn Sie ein altes Konto bei IVARIO haben (erstellt vor Oktober 2025), gelangen Sie ',
    sampleListLink: 'hier',
    sampleListOutro:
      ' zu unserem alten System, um Ihre registrierten Proben und Analyseberichte einzusehen.',
  },
} as const;
